import React, {useCallback, useEffect, useState} from 'react';
import createAxiosInstance from "../support/AxiosSupport";
import {pluralItemsOf} from '../utils/plural';

interface ColorCount {
    colorId: string;
    code: string | null;
    colorName: string | null;
    count: number;
    percent: number;
}

interface ColorPalette {
    totalItems: number;
    coloredItems: number;
    colors: ColorCount[];
}

interface Props {
    ownerId: string | null;
}

// Сопоставление кодов цветов (ref_clothes_color) со swatch-цветами.
const SWATCH: Record<string, string> = {
    BLACK: '#111827', WHITE: '#f3f4f6', BLUE: '#2563eb', RED: '#dc2626',
    GREEN: '#16a34a', YELLOW: '#eab308', ORANGE: '#ea580c', PINK: '#ec4899',
    GREY: '#9ca3af', BROWN: '#92400e', PURPLE: '#7c3aed',
};

// Swatch для известного кода — сплошной цвет; для неизвестного (есть в
// ref_clothes_color, но нет в SWATCH) — нейтральная диагональная штриховка,
// чтобы он визуально не сливался с настоящим серым (GREY).
const HATCH =
    'repeating-linear-gradient(45deg, #9ca3af 0, #9ca3af 2px, transparent 2px, transparent 4px)';

const swatchStyle = (code: string | null): React.CSSProperties => {
    const hex = code ? SWATCH[code] : undefined;
    return hex
        ? {backgroundColor: hex}
        : {backgroundColor: '#e5e7eb', backgroundImage: HATCH};
};

// 'solid' — известный цвет, 'hatched' — неизвестный (штриховка-плейсхолдер).
const swatchKind = (code: string | null): 'solid' | 'hatched' =>
    code && SWATCH[code] ? 'solid' : 'hatched';

/**
 * Самостоятельный блок «Палитра гардероба»: распределение цветов активных вещей со
 * swatch-индикаторами и долями. Грузит данные сам; некритичный — при ошибке/пустоте скрыт.
 */
const ColorPaletteSection: React.FC<Props> = ({ownerId}) => {
    const [data, setData] = useState<ColorPalette | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const api = await createAxiosInstance();
            const params = ownerId ? {ownerId} : {};
            const res = await api.get<ColorPalette>('/color-palette', {params});
            setData(res.data);
        } catch (e) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [ownerId]);

    useEffect(() => {
        load();
    }, [load]);

    if (loading) {
        return (
            <section data-testid="color-palette-loading" className="animate-pulse">
                <div className="h-5 w-40 bg-gray-200 dark:bg-ink-700 rounded mb-3"/>
                <div className="h-20 bg-gray-100 dark:bg-ink-800 rounded-xl"/>
            </section>
        );
    }

    // Некритичный блок: при ошибке/отсутствии цветов просто не показываем.
    if (error || !data || data.colors.length === 0) {
        return null;
    }

    return (
        <section data-testid="color-palette" className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Палитра гардероба · {data.coloredItems} из {pluralItemsOf(data.totalItems)}
            </h2>
            <ul className="space-y-2">
                {data.colors.map(c => (
                    <li key={c.colorId} className="flex items-center gap-3" data-testid={`palette-color-${c.code ?? 'unknown'}`}>
                        <span
                            className="shrink-0 w-5 h-5 rounded-full border border-gray-300 dark:border-ink-600"
                            style={swatchStyle(c.code)}
                            data-swatch-kind={swatchKind(c.code)}
                            aria-hidden="true"
                        />
                        <span className="w-28 shrink-0 text-sm text-gray-900 dark:text-gray-100 truncate">
                            {c.colorName ?? c.code ?? '—'}
                        </span>
                        <span className="flex-1 h-3 rounded-full bg-gray-100 dark:bg-ink-700 overflow-hidden">
                            <span
                                className="block h-full bg-brand-500 dark:bg-brand-400"
                                style={{width: `${c.percent}%`}}
                            />
                        </span>
                        <span className="w-16 shrink-0 text-right text-xs text-gray-500 dark:text-gray-400">
                            {c.count} · {c.percent}%
                        </span>
                    </li>
                ))}
            </ul>
        </section>
    );
};

export default ColorPaletteSection;
